import { flexRender } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  TableBody,
  TableHead,
  TableHeader,
  Table,
  TableRow,
} from "~/components/table";
import { IProject } from "~/integrations/firebase/collections";
import { useProjects } from "~/pages/projects/contexts/projects-context";
import { useTable } from "~/hooks/use-table";
import { columns } from "./columns";
import { PreHeader } from "./pre-header";
import { ProjectsTableBody } from "./projects-table-body";
import { PaginationController } from "~/components/pagination-controller/pagination-controller";

interface ProjectsTableProps {
  onRowClicked: (project: IProject) => void;
}

const emptyArray: IProject[] = [];

export const ProjectsTable = ({ onRowClicked }: ProjectsTableProps) => {
  const { projects, isLoadingRead, errorRead, count, page, pageSize } =
    useProjects();

  const { t } = useTranslation(["components"]);

  const displayProjects = projects.slice(
    (page - 1) * pageSize,
    (page - 1) * pageSize + pageSize,
  );

  const { table } = useTable({
    columns: columns(t),
    data: displayProjects || emptyArray,
    totalRecords: count || 0,
    getRowId: (row) => row.id,
  });

  return (
    <div className="space-y-4 text-left">
      <PreHeader />
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <ProjectsTableBody
            isLoadingRead={isLoadingRead}
            errorRead={errorRead}
            projects={projects}
            table={table}
            columns={columns(t)}
            onRowClicked={onRowClicked}
          />
        </TableBody>
      </Table>

      <PaginationController itemsCount={count} />
    </div>
  );
};
